import { useMemo, useState } from 'react';
import { Layout, Menu, Dropdown, Space, Typography, Badge, Tooltip, Button, Drawer } from 'antd';
import {
  DashboardOutlined,
  AuditOutlined,
  AccountBookOutlined,
  ClearOutlined,
  TeamOutlined,
  CrownOutlined,
  CreditCardOutlined,
  TransactionOutlined,
  GiftOutlined,
  ShareAltOutlined,
  UsergroupAddOutlined,
  MobileOutlined,
  MergeCellsOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  LogoutOutlined,
  UserOutlined,
  FormOutlined,
  HistoryOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { get } from '../api/client';

const { Header, Sider, Content } = Layout;

const MENU_ITEMS = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表盘' },
  { key: '/approvals', icon: <AuditOutlined />, label: '审核管理' },
  { key: '/users', icon: <TeamOutlined />, label: '用户管理' },
  { key: '/customers', icon: <UserOutlined />, label: '客户管理' },
  { key: '/order-notes', icon: <FormOutlined />, label: '订单备注' },
  { key: '/order-history', icon: <HistoryOutlined />, label: '订单留痕' },
  { key: '/membership', icon: <CrownOutlined />, label: '会员管理' },
  { key: '/payment', icon: <CreditCardOutlined />, label: '支付管理' },
  { key: '/payment-transfers', icon: <TransactionOutlined />, label: '转账审批' },
  { key: '/discount-coupons', icon: <GiftOutlined />, label: '优惠券' },
  { key: '/promotion-links', icon: <ShareAltOutlined />, label: '推广链接' },
  { key: '/referrals', icon: <UsergroupAddOutlined />, label: '邀请记录' },
  { key: '/phone-releases', icon: <MobileOutlined />, label: '手机号释放' },
  { key: '/identity-merges', icon: <MergeCellsOutlined />, label: '合并台账' },
  { key: '/annual-archive', icon: <AccountBookOutlined />, label: '年度归档' },
  { key: '/file-cleanup', icon: <ClearOutlined />, label: '文件清理' },
  { key: '/settings/register-flow', icon: <SafetyCertificateOutlined />, label: '注册流程' },
  { key: '/settings/credentials', icon: <KeyOutlined />, label: '服务凭据' },
  { key: '/settings', icon: <SettingOutlined />, label: '系统配置' },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  // 窄屏（<992px）由 Sider 的 breakpoint 回调告知；此时导航整个收起，改由抽屉承载
  const [isNarrow, setIsNarrow] = useState(false);
  // 宽屏下的图标栏模式（216px ↔ 64px）
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // 待审人工申请数量轮询（30s），驱动顶栏铃铛角标
  const { data: pendingCount } = useQuery({
    queryKey: ['manual-application', 'pending-count'],
    queryFn: () => get<{ count: number }>('/manual-application/stats/pending-count'),
    refetchInterval: 30_000,
    retry: false,
  });

  const selectedKey = useMemo(() => {
    // 取最长前缀匹配，避免 /payment-transfers 命中 /payment、/settings/xxx 命中 /settings
    const match = MENU_ITEMS.filter((item) => location.pathname.startsWith(item.key)).sort(
      (a, b) => b.key.length - a.key.length,
    )[0];
    return match?.key ?? '/dashboard';
  }, [location.pathname]);

  const siderCollapsed = isNarrow || railCollapsed;

  const onMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    setNavOpen(false);
  };

  const currentTitle = MENU_ITEMS.find((item) => item.key === selectedKey)?.label ?? '';

  const userMenu = {
    items: [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        onClick: async () => {
          await signOut();
          navigate('/login', { replace: true });
        },
      },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={216}
        theme="dark"
        collapsible
        trigger={null}
        breakpoint="lg"
        collapsedWidth={isNarrow ? 0 : 64}
        collapsed={siderCollapsed}
        onBreakpoint={setIsNarrow}
        onCollapse={(value, type) => {
          // 窄屏的收展由 breakpoint 驱动（isNarrow），只有用户主动点折叠才改图标栏状态
          if (type === 'clickTrigger') setRailCollapsed(!value);
        }}
      >
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 600,
            fontSize: 16,
            letterSpacing: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {siderCollapsed ? '精英' : '活动精英 · 管理后台'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={MENU_ITEMS}
          onClick={onMenuClick}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingInline: isNarrow ? 12 : 24,
            borderBottom: '1px solid #eee',
          }}
        >
          <Button
            type="text"
            aria-label="导航菜单"
            icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => (isNarrow ? setNavOpen(true) : setRailCollapsed((v) => !v))}
          />
          {/* 导航收起后这里是唯一的「我在哪一页」线索；窄屏省空间时优先保它 */}
          <Typography.Text type="secondary" style={{ flex: 1, minWidth: 0 }} ellipsis>
            {currentTitle}
          </Typography.Text>
          <Tooltip title={pendingCount && pendingCount.count > 0 ? `${pendingCount.count} 条人工申请待审核` : '暂无待审申请'}>
            <Badge count={pendingCount?.count ?? 0} size="small" offset={[2, -2]}>
              <BellOutlined
                style={{ fontSize: 18, cursor: 'pointer' }}
                onClick={() => navigate('/approvals')}
              />
            </Badge>
          </Tooltip>
          <Dropdown menu={userMenu} placement="bottomRight">
            <Space style={{ cursor: 'pointer', marginLeft: isNarrow ? 4 : 20 }}>
              <UserOutlined />
              {!isNarrow && (
                <Typography.Text>{user?.fullName || user?.username || '管理员'}</Typography.Text>
              )}
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ padding: isNarrow ? 12 : 24, overflow: 'auto' }}>
          <Outlet />
        </Content>
      </Layout>
      {/* 窄屏导航：216px 侧栏在手机上会吃掉近四成视口，改成点开才出现的抽屉 */}
      <Drawer
        title="活动精英 · 管理后台"
        placement="left"
        width={240}
        open={isNarrow && navOpen}
        onClose={() => setNavOpen(false)}
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={MENU_ITEMS}
          onClick={onMenuClick}
          style={{ borderInlineEnd: 'none' }}
        />
      </Drawer>
    </Layout>
  );
}